import { useState } from 'react'
import type { AutonomyPage, Row } from '../../api/settingsAutonomy'
import { n, post, s, useMsg } from './AutonomyPage'

/**
 * "Ask channel" (F10): aplicatia clientului (ask.optimumdesk.ai) si chatul de
 * pe statie ca surse ale biroului AI. Salvarea merge la `/desk/ask` si scrie
 * numai campurile canalului, peste politica in vigoare - comutatoarele din
 * "Service desk" raman cum sunt. In modul `live` Adam raspunde in aplicatie, in
 * timp real, in limitele de aici (runde, discutii pe zi, minute de pauza,
 * model); serverul refuza o limita din afara intervalului, nu o strange tacut.
 */
export function AskChannelPanel({ k, companyId, companyName, onChange }: { k: AutonomyPage['desk']; companyId: number; companyName: string; onChange(): void }) {
  const p: Row = k.policy ?? {}
  const [msg, say] = useMsg()
  const [f, setF] = useState({
    ask_mode: s(p.ask_mode) === 'live' ? 'live' : 'register',
    live_max_rounds: String(p.live_max_rounds ?? 12),
    live_max_per_user_day: String(p.live_max_per_user_day ?? 10),
    live_idle_minutes: String(p.live_idle_minutes ?? 10),
    live_tier: s(p.live_tier) === 'low' ? 'low' : 'medium',
    chat_too: Boolean(n(p.chat_too)),
    chat_brain: s(p.chat_brain) === 'desk' ? 'desk' : 'legacy',
    kb_cite: p.kb_cite === undefined ? true : Boolean(n(p.kb_cite)),
    ask_email_twin: p.ask_email_twin === undefined ? true : Boolean(n(p.ask_email_twin)),
  })
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch })
  const live = f.ask_mode === 'live'
  const brainForced = f.chat_too || live
  const brain = brainForced ? 'desk' : f.chat_brain
  const askOn = p.ask_too === undefined ? true : Boolean(n(p.ask_too))

  const save = () => {
    post('/desk/ask', {
      scope: k.scope,
      ask_mode: f.ask_mode,
      ...(live ? { live_max_rounds: f.live_max_rounds, live_max_per_user_day: f.live_max_per_user_day, live_idle_minutes: f.live_idle_minutes, live_tier: f.live_tier } : {}),
      chat_too: f.chat_too ? '1' : '0',
      chat_brain: brain,
      kb_cite: f.kb_cite ? '1' : '0',
      ask_email_twin: f.ask_email_twin ? '1' : '0',
    }, say, () => setTimeout(onChange, 600))
  }

  return (
    <div className="panel panel-default" id="odask" data-scope={k.scope}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Ask channel</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            requests from the customer app and from the station chat, answered by the same AI desk with the same rules as above. The actions a request may lead to follow one ladder for every customer channel - the email setting in Service desk.
          </span>
        </div>
        <div style={{ fontSize: 12, marginTop: 6 }}>
          Customer app requests are <strong>{askOn ? 'on' : 'off'}</strong> for {k.scope === 'sp' ? 'all companies' : companyName || `#${companyId}`} (the switch is in Service desk).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '12px 24px', marginTop: 12, fontSize: 12 }}>
          <div>
            <div><strong>Customer app</strong></div>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="odask-mode" value="register" checked={!live} onChange={() => set({ ask_mode: 'register' })} /> <strong>register</strong> - the request becomes a ticket and the desk answers on it, in the app and by email
            </label>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="odask-mode" value="live" checked={live} onChange={() => set({ ask_mode: 'live' })} /> <strong>live</strong> - the person talks with the desk in the app, in real time; colleagues see it on the ticket and can take over
            </label>
            {live && (
              <div id="odask-live" style={{ display: 'grid', gridTemplateColumns: 'auto 80px', gap: '6px 8px', alignItems: 'center', margin: '8px 0 0 20px' }}>
                <label htmlFor="odask-rounds">Rounds per conversation (1-50)</label>
                <input type="number" id="odask-rounds" className="form-control input-sm" min={1} max={50} value={f.live_max_rounds} onChange={(e) => set({ live_max_rounds: e.target.value })} />
                <label htmlFor="odask-per-day">Live conversations per person a day (0-100)</label>
                <input type="number" id="odask-per-day" className="form-control input-sm" min={0} max={100} value={f.live_max_per_user_day} onChange={(e) => set({ live_max_per_user_day: e.target.value })} />
                <label htmlFor="odask-idle">Idle minutes before the live badge goes (1-60)</label>
                <input type="number" id="odask-idle" className="form-control input-sm" min={1} max={60} value={f.live_idle_minutes} onChange={(e) => set({ live_idle_minutes: e.target.value })} />
                <label htmlFor="odask-tier">Model</label>
                <select id="odask-tier" className="form-control input-sm" value={f.live_tier} onChange={(e) => set({ live_tier: e.target.value })}>
                  <option value="low">low cost</option>
                  <option value="medium">medium</option>
                </select>
              </div>
            )}
            <label style={{ display: 'block', marginTop: 8 }}>
              <input type="checkbox" id="odask-twin" checked={f.ask_email_twin} onChange={(e) => set({ ask_email_twin: e.target.checked })} /> also send the desk&apos;s answers by email to the person, with a link back to the app
            </label>
            <label style={{ display: 'block', marginTop: 6 }}>
              <input type="checkbox" id="odask-cite" checked={f.kb_cite} onChange={(e) => set({ kb_cite: e.target.checked })} /> the desk may cite the company&apos;s published Self-Service articles and catalog services in its answers
            </label>
          </div>
          <div>
            <div><strong>Station chat</strong></div>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="checkbox" id="odask-chat-too" checked={f.chat_too} onChange={(e) => set({ chat_too: e.target.checked })} /> the AI desk also takes tickets opened from the station chat
            </label>
            <div style={{ marginTop: 8 }}>Who answers the chat</div>
            <label style={{ display: 'block', marginTop: 4, opacity: brainForced ? 0.55 : 1 }}>
              <input type="radio" name="odask-brain" value="legacy" checked={brain === 'legacy'} disabled={brainForced} onChange={() => set({ chat_brain: 'legacy' })} /> the chat assistant, as today
            </label>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="odask-brain" value="desk" checked={brain === 'desk'} disabled={brainForced} onChange={() => set({ chat_brain: 'desk' })} /> the AI desk
              {f.chat_too ? ' - required while the desk takes chat tickets' : live ? ' - required in live mode' : ''}
            </label>
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn btn-primary btn-sm" id="odask-save" onClick={save}>Save</button>
          {msg}
        </div>
      </div>
    </div>
  )
}
